import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Check, Info, Loader2, RefreshCw, Save, Settings, Thermometer, Zap, Server, Sparkles } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { getRAGConfiguration, getBulkConfigurations } from '@/services/ai-configuration-client-service';

// Model options available
const AVAILABLE_MODELS = [
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fastest and most cost-effective model' },
  { id: 'gpt-4o', name: 'GPT-4o', description: 'More powerful model with better reasoning' },
  { id: 'gpt-4.5-preview', name: 'GPT-4.5 Preview', description: 'Most advanced model (higher cost)' }
];

// Mock config data structure - will be replaced with actual API calls
interface ConfigItem {
  id: string;
  key: string;
  value: any;
  description: string;
}

export const ConfigurationManagement = () => {
  const [activeTab, setActiveTab] = useState('rag'); // Default to RAG tab
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [configItems, setConfigItems] = useState<ConfigItem[]>([]);
  const [configSummary, setConfigSummary] = useState({
    temperature: 0.7,
    maxTokens: 800,
    similarityThreshold: 0.45,
    model: 'gpt-4o-mini',
    enableQueryExpansion: true,
    enableCrossEncoder: false,
    maxKnowledgeResults: 8
  });

  // Fetch configuration from database
  const fetchConfiguration = async () => {
    setIsLoading(true);
    try {
      // Fetch RAG configuration first
      const ragConfig = await getRAGConfiguration();
      
      // Fetch other summary values using bulk configuration
      const summaryValues = await getBulkConfigurations({
        temperature: 0.7,
        max_tokens: 800,
        model: 'gpt-4o-mini'
      });

      setConfigSummary({
        temperature: summaryValues.temperature,
        maxTokens: summaryValues.max_tokens,
        similarityThreshold: ragConfig.similarityThreshold,
        model: summaryValues.model || 'gpt-4o-mini',
        enableQueryExpansion: ragConfig.enableQueryExpansion,
        enableCrossEncoder: ragConfig.enableCrossEncoder,
        maxKnowledgeResults: ragConfig.maxKnowledgeResults
      });

      // Then fetch all configurations
      const { data, error } = await supabase
        .from('ai_configuration')
        .select('*');
        
      if (error) {
        throw error;
      }
      
      setConfigItems(data || []);
    } catch (error) {
      console.error('Error fetching configuration:', error);
      toast.error('Failed to load configuration data');
    } finally {
      setIsLoading(false);
    }
  };

  // Update configuration item
  const updateConfigItem = async (id: string, newValue: any) => {
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('ai_configuration')
        .update({ value: newValue })
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      setConfigItems(items => 
        items.map(item => 
          item.id === id ? { ...item, value: newValue } : item
        )
      );

      // Refresh summary
      fetchConfiguration();
      
      toast.success('Configuration updated');
    } catch (error) {
      console.error('Error updating configuration:', error);
      toast.error('Failed to update configuration');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle value change in form
  const handleValueChange = (id: string, newValue: any) => {
    setConfigItems(items => 
      items.map(item => 
        item.id === id ? { ...item, value: newValue } : item
      )
    );
  };

  // Save all changes
  const saveChanges = async () => {
    setIsSaving(true);
    
    try {
      // Create an array of promises for each update
      const updatePromises = configItems.map(item => 
        supabase
          .from('ai_configuration')
          .update({ value: item.value })
          .eq('id', item.id)
      );
      
      // Execute all updates in parallel
      await Promise.all(updatePromises);
      
      // Refresh summary after updates
      fetchConfiguration();
      
      toast.success('All changes saved successfully');
    } catch (error) {
      console.error('Error saving configuration changes:', error);
      toast.error('Failed to save some configuration changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchConfiguration();
  }, []);

  // Helper function to render config input based on value type
  const renderConfigInput = (item: ConfigItem) => {
    // Special case for model selection
    if (item.key === 'model') {
      return (
        <Select
          value={item.value}
          onValueChange={value => handleValueChange(item.id, value)}
        >
          <SelectTrigger className="max-w-md">
            <SelectValue placeholder="Select model" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_MODELS.map(model => (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex flex-col">
                  <span>{model.name}</span>
                  <span className="text-xs text-muted-foreground">{model.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    
    const valueType = typeof item.value;
    
    if (valueType === 'boolean') {
      return (
        <div className="flex items-center space-x-2">
          <Switch 
            id={`config-${item.id}`}
            checked={item.value}
            onCheckedChange={checked => handleValueChange(item.id, checked)}
          />
          <Label htmlFor={`config-${item.id}`}>
            {item.value ? 'Enabled' : 'Disabled'}
          </Label>
        </div>
      );
    }
    
    if (valueType === 'number') {
      return (
        <Input 
          id={`config-${item.id}`}
          type="number"
          value={item.value}
          onChange={e => handleValueChange(item.id, parseFloat(e.target.value))}
          className="max-w-md"
        />
      );
    }
    
    if (valueType === 'string') {
      if (item.value.length > 100) {
        return (
          <Textarea 
            id={`config-${item.id}`}
            value={item.value}
            onChange={e => handleValueChange(item.id, e.target.value)}
            className="font-mono text-sm"
            rows={5}
          />
        );
      }
      
      return (
        <Input 
          id={`config-${item.id}`}
          value={item.value}
          onChange={e => handleValueChange(item.id, e.target.value)}
          className="max-w-md"
        />
      );
    }
    
    // For object values (JSON)
    return (
      <Textarea 
        id={`config-${item.id}`}
        value={JSON.stringify(item.value, null, 2)}
        onChange={e => {
          try {
            const newValue = JSON.parse(e.target.value);
            handleValueChange(item.id, newValue);
          } catch (err) {
            // Allow invalid JSON during editing, but don't update the value
          }
        }}
        className="font-mono text-sm"
        rows={5}
      />
    );
  };

  // Filter configs based on active tab
  const getConfigsForTab = (tab: string) => {
    if (tab === 'all') {
      return configItems;
    }
    
    const tabKeyPrefixes: Record<string, string[]> = {
      'rag': ['similarity', 'enable_query_expansion', 'enable_cross_encoder', 'max_knowledge_results', 'progressive_threshold'],
      'general': ['cache', 'default'],
      'model': ['temperature', 'max_tokens', 'model'],
      'modes': ['expert_mode_description', 'conversation_mode_description'],
      'advanced': ['regex', 'pattern']
    };
    
    const prefixes = tabKeyPrefixes[tab] || [];
    return configItems.filter(item => 
      prefixes.some(prefix => item.key.includes(prefix))
    );
  };

  // Get current model info
  const getCurrentModelInfo = () => {
    const modelId = configSummary.model;
    return AVAILABLE_MODELS.find(model => model.id === modelId) || AVAILABLE_MODELS[0];
  };

  return (
    <div className="space-y-6">
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle>AI Configuration Summary</CardTitle>
          <CardDescription>
            Critical configuration values affecting AI assistant behavior
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg">
                <Server className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Similarity Threshold</p>
                  <p className="text-2xl font-bold">{configSummary.similarityThreshold}</p>
                  <p className="text-xs text-muted-foreground">Knowledge matching</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg">
                <Zap className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium">Query Expansion</p>
                  <p className="text-2xl font-bold">{configSummary.enableQueryExpansion ? 'ON' : 'OFF'}</p>
                  <p className="text-xs text-muted-foreground">Better Vietnamese search</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg">
                <Thermometer className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm font-medium">Temperature</p>
                  <p className="text-2xl font-bold">{configSummary.temperature}</p>
                  <p className="text-xs text-muted-foreground">Creativity level</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-sm font-medium">Model</p>
                  <p className="text-2xl font-bold">{getCurrentModelInfo().name}</p>
                  <p className="text-xs text-muted-foreground">{getCurrentModelInfo().description}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>AI System Configuration</CardTitle>
          <CardDescription>
            Manage configuration settings for the AI assistant system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="rag">Knowledge RAG</TabsTrigger>
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="model">Model Parameters</TabsTrigger>
              <TabsTrigger value="modes">AI Modes</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
              <TabsTrigger value="all">All Settings</TabsTrigger>
            </TabsList>
            
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  {activeTab === 'rag' && 'Knowledge Retrieval and Generation Settings'}
                  {activeTab === 'general' && 'General Configuration Settings'}
                  {activeTab === 'model' && 'AI Model Parameters'}
                  {activeTab === 'modes' && 'AI Assistant Modes Configuration'}
                  {activeTab === 'advanced' && 'Advanced System Configuration'}
                  {activeTab === 'all' && 'All Configuration Settings'}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchConfiguration}
                  disabled={isLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  size="sm"
                  onClick={saveChanges}
                  disabled={isSaving || isLoading}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save All Changes
                </Button>
              </div>
            </div>
            
            {activeTab === 'rag' && (
              <Alert className="mb-6 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertTitle>Knowledge RAG Configuration</AlertTitle>
                <AlertDescription>
                  These settings control how the AI retrieves and uses knowledge base content. Lower similarity threshold = more matches for Vietnamese queries.
                </AlertDescription>
              </Alert>
            )}
            
            {activeTab === 'model' && (
              <Alert className="mb-6 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertTitle>Model Configuration</AlertTitle>
                <AlertDescription>
                  Changing the AI model affects cost and performance. More powerful models increase costs but provide better responses.
                </AlertDescription>
              </Alert>
            )}
            
            {activeTab === 'modes' && (
              <div className="space-y-8">
                <div className="space-y-4">
                  <h3 className="text-base font-medium">Expert Mode Configuration</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Configure how the AI assistant behaves in Expert Mode
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="expert_mode_description">Expert Mode Description</Label>
                    <Textarea 
                      id="expert_mode_description"
                      placeholder="Configure how the AI should behave in Expert Mode"
                      className="min-h-[120px]"
                      value={
                        configItems.find(item => item.key === 'expert_mode_description')?.value || 
                        "Be detailed, thorough and precise in your answers. Provide well-structured information with clear examples where appropriate."
                      }
                      onChange={(e) => {
                        const item = configItems.find(item => item.key === 'expert_mode_description');
                        if (item) {
                          handleValueChange(item.id, e.target.value);
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This description will be added to the AI's system instructions when in Expert Mode
                    </p>
                  </div>
                  <div className="space-y-2 mt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="enforce_suggestions_expert">Enforce Suggestions</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Always show suggestion buttons after AI responses in Expert Mode
                        </p>
                      </div>
                      <Switch 
                        id="enforce_suggestions_expert"
                        checked={
                          configItems.find(item => item.key === 'enforce_suggestions')?.value || true
                        }
                        onCheckedChange={(checked) => {
                          const item = configItems.find(item => item.key === 'enforce_suggestions');
                          if (item) {
                            handleValueChange(item.id, checked);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-base font-medium">Conversation Mode Configuration</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Configure how the AI assistant behaves in Conversation Mode
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="conversation_mode_description">Conversation Mode Description</Label>
                    <Textarea 
                      id="conversation_mode_description"
                      placeholder="Configure how the AI should behave in Conversation Mode"
                      className="min-h-[120px]"
                      value={
                        configItems.find(item => item.key === 'conversation_mode_description')?.value || 
                        "Be friendly, casual and conversational. Use an engaging tone and keep responses concise. Feel free to use appropriate emojis and conversational language."
                      }
                      onChange={(e) => {
                        const item = configItems.find(item => item.key === 'conversation_mode_description');
                        if (item) {
                          handleValueChange(item.id, e.target.value);
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This description will be added to the AI's system instructions when in Conversation Mode
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            ) : getConfigsForTab(activeTab).length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>No configuration items found</AlertTitle>
                <AlertDescription>
                  No configuration settings were found for this category.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-6">
                {getConfigsForTab(activeTab).map(item => (
                  <div key={item.id} className="space-y-2">
                    <Label htmlFor={`config-${item.id}`} className="text-base font-medium">
                      {item.key}
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      {item.description}
                    </p>
                    {renderConfigInput(item)}
                  </div>
                ))}
              </div>
            )}
          </Tabs>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Advanced Configuration</CardTitle>
          <CardDescription>
            Additional settings for system optimization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Important Note</AlertTitle>
            <AlertDescription>
              These are advanced settings that affect the behavior of the AI system. Adjust with caution.
            </AlertDescription>
          </Alert>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-1">Cache Management</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Control how the system caches AI responses
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button variant="outline" onClick={() => toast.info('Cache flushing not implemented yet')}>
                  Flush All Cache
                </Button>
                <Button variant="outline" onClick={() => toast.info('Cache stats not implemented yet')}>
                  View Cache Statistics
                </Button>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <h3 className="font-medium mb-1">System Test & Diagnostics</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Test system components and connections
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button variant="outline" onClick={() => toast.success('All systems operational')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Run System Diagnostics
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
